import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Zap, TrendingUp, Calendar, IndianRupee } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { calculateSolarSavings } from '../utils/calculatorEngine';

export const SavingsCalculator = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    city: '',
    monthly_bill: '',
    roof_type: '',
    phase_type: 'single'
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Parse URL parameters and pre-fill form if values exist
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const city = params.get('city');
    const monthly_bill = params.get('monthly_bill');
    const roof_type = params.get('roof_type');

    if (city || monthly_bill || roof_type) {
      setFormData(prev => ({
        ...prev,
        city: city || '',
        monthly_bill: monthly_bill || '',
        roof_type: roof_type || ''
      }));
    }
  }, [location.search]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = calculateSolarSavings(parseFloat(formData.monthly_bill));
      setResult(result);
    } catch (error) {
      console.error('Calculator error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <section className="py-20 md:py-32" data-testid="calculator-hero">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Calculator className="w-16 h-16 text-primary mx-auto mb-6" />
            <h1 className="text-5xl md:text-7xl font-heading font-semibold text-primary tracking-tight leading-[1.1] mb-6">
              Solar Savings Calculator
            </h1>
            <p className="text-lg md:text-xl text-stone-600 leading-relaxed">
              Discover how much you can save by switching to solar. Get instant estimates on system size, costs, and payback period.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-hover"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">District</label>
                  <select
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    required
                    data-testid="calc-city-input"
                  >
                    <option value="">Select District</option>
                    <option value="Adilabad">Adilabad</option>
                      <option value="Bhadradri Kothagudem">Bhadradri Kothagudem</option>
                      <option value="Hanumakonda">Hanumakonda</option>
                      <option value="Hyderabad">Hyderabad</option>
                      <option value="Jagtial">Jagtial</option>
                      <option value="Jangaon">Jangaon</option>
                      <option value="Jayashankar Bhupalpally">Jayashankar Bhupalpally</option>
                      <option value="Jogulamba Gadwal">Jogulamba Gadwal</option>
                      <option value="Kamareddy">Kamareddy</option>
                      <option value="Karimnagar">Karimnagar</option>
                      <option value="Khammam">Khammam</option>
                      <option value="Kumuram Bheem">Kumuram Bheem</option>
                      <option value="Mahabubabad">Mahabubabad</option>
                      <option value="Mahabubnagar">Mahabubnagar</option>
                      <option value="Mancherial">Mancherial</option>
                      <option value="Medak">Medak</option>
                      <option value="Medchal-Malkajgiri">Medchal-Malkajgiri</option>
                      <option value="Mulugu">Mulugu</option>
                      <option value="Nagarkurnool">Nagarkurnool</option>
                      <option value="Nalgonda"> Nalgonda</option>
                      <option value="Narayanpet">Narayanpet</option>
                      <option value="Nirmal">Nirmal</option>
                      <option value="Nizamabad">Nizamabad</option>
                      <option value="Peddapalli">Peddapalli</option>
                      <option value="Rajanna Sircilla">Rajanna Sircilla</option>
                      <option value="Rangareddy">Rangareddy</option>
                      <option value="Sangareddy">Sangareddy</option>
                      <option value="Siddipet">Siddipet</option>
                      <option value="Suryapet">Suryapet</option>
                      <option value="Vikarabad">Vikarabad</option>
                      <option value="Wanaparthy">Wanaparthy</option>
                      <option value="Warangal">Warangal</option>
                      <option value="Yadadri Bhuvanagiri">Yadadri Bhuvanagiri</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Monthly Electricity Bill (₹)</label>
                  <input
                    type="number"
                    value={formData.monthly_bill}
                    onChange={(e) => setFormData({...formData, monthly_bill: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="e.g., 5000"
                    required
                    data-testid="calc-bill-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Roof Type</label>
                  <select
                    value={formData.roof_type}
                    onChange={(e) => setFormData({...formData, roof_type: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    required
                    data-testid="calc-roof-input"
                  >
                    <option value="">Select Roof Type</option>
                    <option value="RCC">RCC/Concrete</option>
                    <option value="Tin">Tin Shed</option>
                    <option value="Terrace">Terrace</option>
                    <option value="Ground">Ground Mount</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Phase Type</label>
                  <select
                    value={formData.phase_type}
                    onChange={(e) => setFormData({...formData, phase_type: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    data-testid="calc-phase-input"
                  >
                    <option value="single">Single Phase</option>
                    <option value="three">Three Phase</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-secondary text-white hover:bg-gradient-to-r hover:from-secondary hover:to-accent rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
                data-testid="calc-submit-button"
              >
                {loading ? 'Calculating...' : 'Calculate Savings'}
              </button>
            </form>

            {result && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-12"
                data-testid="calc-results"
              >
                <h3 className="text-2xl font-heading font-semibold text-primary mb-6 text-center">
                  Your Solar Savings Report
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-lime-50 to-emerald-50 rounded-2xl p-6 border border-lime-200">
                    <Zap className="w-8 h-8 text-accent mb-3" />
                    <p className="text-sm text-stone-600 mb-1">Recommended System Size</p>
                    <p className="text-3xl font-bold text-primary">{result.system_size_kw} kW</p>
                  </div>

                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-200">
                    <IndianRupee className="w-8 h-8 text-secondary-foreground mb-3" />
                    <p className="text-sm text-stone-600 mb-1">Monthly Savings</p>
                    <p className="text-3xl font-bold text-primary">₹{result.monthly_savings.toFixed(0)}</p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200">
                    <TrendingUp className="w-8 h-8 text-blue-600 mb-3" />
                    <p className="text-sm text-stone-600 mb-1">Yearly Savings</p>
                    <p className="text-3xl font-bold text-primary">₹{result.yearly_savings.toFixed(0)}</p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                    <Calendar className="w-8 h-8 text-purple-600 mb-3" />
                    <p className="text-sm text-stone-600 mb-1">Payback Period</p>
                    <p className="text-3xl font-bold text-primary">{result.payback_period} years</p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-stone-200">
                  <h4 className="font-heading font-semibold text-primary mb-4">Cost Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-stone-600">Total System Cost</span>
                      <span className="font-semibold text-primary">₹{result.total_cost.toFixed(0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">Government Subsidy</span>
                      <span className="font-semibold text-accent">- ₹{result.govt_subsidy.toFixed(0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">DISCOM Feasibility Charges</span>
                      <span className="font-semibold text-primary">+ ₹{result.discom_feasibility_charges.toFixed(0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-600">MNRE Subsidy Application Charges</span>
                      <span className="font-semibold text-primary">+ ₹{result.mnre_subsidy_application_charges.toFixed(0)}</span>
                    </div>
                    <div className="border-t border-stone-200 pt-3 flex justify-between">
                      <span className="font-semibold text-stone-700">Effective Cost After Subsidy</span>
                      <span className="font-bold text-primary text-xl">₹{result.net_cost.toFixed(0)}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-stone-50 rounded-2xl">
                  <p className="text-sm text-stone-600 mb-4">
                    <strong>Note:</strong> 
                    <ul className="list-disc ml-5 mt-2 space-y-1">
                      <li>Government subsidy is based on the MNRE subsidy table for your system capacity</li>
                      <li>DISCOM Feasibility Charges: ₹2,950 (to be paid online)</li>
                      <li>MNRE Subsidy Application Charges: ₹1,770 (to be paid online)</li>
                      <li>These calculations are estimates based on average consumption patterns, sun hours, and current electricity tariffs. Actual savings may vary.</li>
                    </ul>
                  </p>
                  <Link
                    to="/contact"
                    className="block w-full text-center bg-secondary text-white hover:bg-gradient-to-r hover:from-secondary hover:to-accent rounded-full px-8 py-3 font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 mt-4"
                    data-testid="calc-contact-cta"
                  >
                    Get Detailed Custom Proposal
                  </Link>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};